<template>
  <div class="page-container">
    <!-- Desktop View -->
    <div class="desktop-view" v-if="!isMobile">
      <div class="page-header">
        <div class="header-content">
          <h1 class="page-title">{{ isBenefitModuleEnabled ? 'My Benefits' : 'My Profile' }}</h1>
          <p class="page-subtitle">{{ progressText }}</p>
        </div>
      </div>

      <!-- Skeleton Loading State (only on first load) -->
      <SkeletonLoader v-if="loading && isFirstLoad" type="benefit-desktop" />

      <!-- Pending Reservation Notice Banner -->
      <notice-banner
        v-if="pendingTotalTransaction > 0 && !isPendingBannerDismissed && (!loading || !isFirstLoad)"
        :dismissible="true"
        class="desktop-pending-banner"
        @dismiss="dismissPendingBanner"
      >
        <template #title>Pending Transaction</template>
        <template #default>
          You have <strong>IDR {{ formatCurrency(pendingTotalTransaction) }}</strong> in pending transactions recorded on your account.
        </template>
      </notice-banner>

      <!-- Top Cards Row (show after loading) -->
      <div class="top-cards-row" :class="{'two-cols': !isBenefitModuleEnabled}" v-if="!loading || !isFirstLoad">
        <!-- Profile Card -->
        <div class="profile-card desktop">
          <div class="profile-card-content">
            <div class="profile-img desktop" @click="goToEditProfile">
              <div class="profile-img-wrapper">
                <img :src="(!imageErrorStatus && image.url) ? image.url : defaultAvatar" alt="Profile" @error="imageError">
              </div>
              <button class="edit-profile-btn desktop" @click.stop="goToEditProfile">
                <img src="/img/member-icon/pen.svg" alt="">
              </button>
            </div>
            <div class="profile-info desktop">
              <h2 class="profile-name">{{ user.first_name }} {{ user.last_name }}</h2>
              <p class="profile-tier">{{ currentTierName && currentTierName !== '-' ? (currentTierName.length > 25 ? currentTierName.substring(0, 25) : currentTierName) : '' }} Member</p>
            </div>
          </div>
        </div>

        <!-- Tier Progress Card (Clickable) -->
        <div class="tier-card-wrapper" @click="openTierModal">
          <h3 class="section-title">Current Tier</h3>
          <div class="selected-tier-card" v-if="selectedTierData">
            <member-tier-progress
              :state="selectedTierData.state"
              :progress-percentage="selectedTierData.progressPercentage"
              :current-amount="selectedTierData.currentAmount"
              :current-tier="selectedTierData.currentTierData"
              :next-tier="selectedTierData.nextTierData"
              :is-selected="true"
            />
            <div class="tier-select-hint">
              <span>Click to change tier</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        <template v-if="isBenefitModuleEnabled">
        <!-- Filter Card -->
        <div class="filter-card desktop">
          <h3 class="card-title">Filter</h3>
          <div class="filter-group">
            <label class="filter-label">Merchant / Outlet</label>
            <div class="custom-select desktop">
              <select v-model="selectedMerchant" :disabled="loading">
                <option value="">All Merchants</option>
                <option v-for="merchant in merchants" :key="merchant.id" :value="merchant.id">
                  {{ merchant.name }}
                </option>
              </select>
              <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
          <div class="button-group">
            <button @click="applyFilterDesktop" class="btn-apply" :disabled="loading">Apply Filter</button>
            <button @click="clearFilterDesktop" class="btn-clear" :disabled="loading">Clear Filter</button>
          </div>
        </div>
        </template>
      </div>

      <!-- Benefits List -->
      <template v-if="isBenefitModuleEnabled">
      <div v-if="!loading || !isFirstLoad" class="benefits-section desktop">
        <h3 class="section-title">{{ selectedTierName === 'Member' ? 'Member Benefits' : `${selectedTierName.length > 25 ? selectedTierName.substring(0, 25) : selectedTierName} Tier Benefits` }}</h3>
        <div class="benefits-accordion-list" v-if="groupedBenefits.length > 0">
          <BenefitAccordion
            v-for="group in groupedBenefits"
            :key="group.id"
            :group="group"
            :isOpen="openGroupId === group.id"
            :isLocked="isBenefitGroupLocked(group)"
            :isSolo="groupedBenefits.length === 1"
            @toggle="handleToggle"
            @benefit-select="handleBenefitSelect"
          />
        </div>
        <div class="benefits-empty" v-else>
          <p>No benefits available for this tier</p>
        </div>
      </div>
      </template>

      <!-- Tier Selection Modal (Desktop Only) -->
      <div v-if="isTierModalOpen" class="modal-overlay desktop-modal" @click="closeTierModal">
        <div class="modal-content tier-modal" @click.stop>
          <div class="modal-header">
            <h3>Select Membership Tier</h3>
            <button @click="closeTierModal" class="close-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <div class="modal-body tier-modal-body">
            <div 
              v-for="(tierItem, index) in tierProgressList" 
              :key="tierItem.id || index"
              class="tier-modal-item"
              :class="{ 'selected': selectedCardIndex === index }"
              @click="selectTierFromModal(index)"
            >
              <member-tier-progress
                :state="tierItem.state"
                :progress-percentage="tierItem.progressPercentage"
                :current-amount="tierItem.currentAmount"
                :current-tier="tierItem.currentTierData"
                :next-tier="tierItem.nextTierData"
                :is-selected="selectedCardIndex === index"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Benefit Detail Modal -->
      <benefit-detail-modal
        v-if="isBenefitModalOpen && selectedBenefitDetail"
        :benefit="selectedBenefitDetail"
        @close="closeBenefitModal"
      />
    </div>

    <!-- Mobile View -->
    <div class="mobile-view" v-else>
      <!-- Skeleton Loading State (Mobile - only on first load) -->
      <SkeletonLoader v-if="loading && isFirstLoad" type="benefit" :count="4" />
      
      <template v-if="!loading || !isFirstLoad">
        <div class="top-background">
            <svg width="330" height="55" viewBox="0 0 330 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="219" cy="-84" rx="219" ry="139" fill="currentColor"/>
            </svg>
        </div>

        <button class="back-button" @click="goBack">
          <img src="/img/member-icon/back-button.svg" alt="back">
        </button>

        <div class="header-section">
          <!-- Profile Photo -->
          <div class="profile-img" @click="goToEditProfile">
              <div class="profile-img-wrapper">
                  <img v-if="image.url && !imageErrorStatus" :src="image.url" alt="Profile" @error="imageError">
                  <div v-else class="member-avatar-placeholder">
                      {{ getInitials }}
                  </div>
              </div>
              <button class="edit-profile-btn" @click.stop="goToEditProfile">
                  <img src="/img/member-icon/pen.svg" alt="">
              </button>
          </div>

          <!-- Text Section -->
          <div class="header-text">
              <h1>{{ user.first_name }} {{ user.last_name }}</h1>
              <p>{{ progressText }}</p>
          </div>
      </div>

      <!-- Tier Progress -->
      <div class="tier-progress" ref="tierProgressContainerMobile" @scroll="handleScroll">
        <member-tier-progress
          v-for="(tierItem, index) in tierProgressList"
          :key="tierItem.id || index"
          :state="tierItem.state"
          :progress-percentage="tierItem.progressPercentage"
          :current-amount="tierItem.currentAmount"
          :current-tier="tierItem.currentTierData"
          :next-tier="tierItem.nextTierData"
          :is-selected="selectedCardIndex === index"
          @click="scrollToCard(index)"
        />
      </div>

      <!-- Pending Notice Mobile Banner (Below Tier Progress) -->
      <notice-banner
        v-if="pendingTotalTransaction > 0 && !isPendingBannerDismissed"
        :dismissible="true"
        class="mobile-pending-banner"
        @dismiss="dismissPendingBanner"
      >
        <template #title>Pending Transaction</template>
        <template #default>
          You have <strong>IDR {{ formatCurrency(pendingTotalTransaction) }}</strong> in pending transactions recorded on your account.
        </template>
      </notice-banner>

      <!-- Benefit Section -->
      <template v-if="isBenefitModuleEnabled">
      <section class="benefits-section">
        <div class="mobile-header">
            <h2 class="mobile-title">{{ selectedTierName === 'Member' ? 'Member Benefits' : `${selectedTierName.length > 25 ? selectedTierName.substring(0, 25) : selectedTierName} Tier Benefits` }}</h2>
            <div class="mobile-actions">
                <button @click="openFilterModal" class="filter-button">
                    <img src="/img/member-icon/filter-icon.svg" alt="filter-icon" width="14" height="14">
                </button>
            </div>
        </div>

        <!-- Filter Modal -->
        <div v-if="isFilterModalOpen" class="modal-overlay" @click="closeFilterModal">
          <div class="modal-content" @click.stop>
            <div class="modal-header">
              <h3>Filter</h3>
              <button @click="closeFilterModal" class="close-button">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <div class="filter-group">
                <label class="filter-label">Merchant / Outlet</label>
                <div class="custom-select">
                  <select v-model="selectedMerchant">
                    <option value="">All Merchants</option>
                    <option v-for="merchant in merchants" :key="merchant.id" :value="merchant.id">
                      {{ merchant.name }}
                    </option>
                  </select>
                  <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div class="button-group">
                <button @click="applyFilter" class="btn-apply">Apply Filter</button>
                <button @click="clearFilter" class="btn-clear">Clear Filter</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Benefits Accordion -->
        <div class="benefits-accordion-list" v-if="groupedBenefits.length > 0">
          <BenefitAccordion
            v-for="group in groupedBenefits"
            :key="group.id"
            :group="group"
            :isOpen="openGroupId === group.id"
            :isLocked="isBenefitGroupLocked(group)"
            :isSolo="groupedBenefits.length === 1"
            @toggle="handleToggle"
            @benefit-select="handleBenefitSelect"
          />
        </div>
        <div class="benefits-empty" v-else>
          <p>No benefits available for this tier</p>
        </div>
      </section>
      </template>
      </template>

      <!-- Benefit Detail Modal (Mobile) -->
      <benefit-detail-modal
        v-if="isBenefitModalOpen && selectedBenefitDetail"
        :benefit="selectedBenefitDetail"
        @close="closeBenefitModal"
      />
    </div>
  </div>
</template>

<script>
import { onMounted, reactive, ref, computed, nextTick, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import MemberTierProgress from '@/components/member/MemberTierProgress.vue';
import BenefitAccordion from '@/components/member/BenefitAccordion.vue';
import BenefitDetailModal from '@/components/member/BenefitDetailModal.vue';
import SkeletonLoader from '@/components/member/SkeletonLoader.vue';
import NoticeBanner from '@/components/member/NoticeBanner.vue';

export default {
  name: 'MemberBenefit',
  components: {
    MemberTierProgress,
    BenefitAccordion,
    BenefitDetailModal,
    SkeletonLoader,
    NoticeBanner
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    const isBenefitModuleEnabled = computed(() => {
        return (store.state.FeatureFlag?.benefitModule?.flag || 'ACTIVE') === 'ACTIVE'
    })

    const loading = ref(false);
    const isFirstLoad = ref(true);

    const tierProgressContainer = ref(null);
    const tierProgressContainerMobile = ref(null);
    const selectedCardIndex = ref(0);
    const activeTierIndex = ref(0);
    const isMobile = ref(window.innerWidth < 992);

    // Handle window resize
    window.addEventListener('resize', () => {
      isMobile.value = window.innerWidth < 992;
    });

    const allTiers = ref([]);
    const totalTransaction = ref(0);
    const pendingTotalTransaction = ref(0);
    const isPendingBannerDismissed = ref(false);
    const currentTierName = ref('');

    function dismissPendingBanner() {
      isPendingBannerDismissed.value = true;
    }

    const user = reactive({
      title: '',
      first_name: '',
      last_name: '',
    });

    const image = reactive({ url: '' });
    const imageErrorStatus = ref(false);
    function imageError() {
      imageErrorStatus.value = true;
    }

    const displayMemberName = computed(() => {
      const u = store.state.Auth.userInfo;
      const firstName = user.first_name || u?.first_name || '';
      const lastName = user.last_name || u?.last_name || '';
      if (firstName || lastName) {
        return `${firstName} ${lastName}`.trim();
      }
      return u?.name || u?.email || '-';
    });

    const defaultAvatar = computed(() => {
      const title = user.title || store.state.Auth.userInfo?.title || '';
      return title === 'Mr.'
        ? 'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/man.png'
        : 'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/woman.png';
    });

    const getInitials = computed(() => {
      const name = displayMemberName.value;
      if (!name || name === '-') return '?';
      const words = name.split(' ').filter(Boolean);
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    });
    const corporate = ref(null);
    const benefits = ref([]);
    const next_benefits = ref([]);
    const next_tier_benefits = ref([]);
    const progressText = ref('');
    const selectedMerchant = ref('');
    const merchants = ref([]);
    const isFilterModalOpen = ref(false);
    const isTierModalOpen = ref(false);
    const isBenefitModalOpen = ref(false);
    const selectedBenefitDetail = ref(null);

    // Format number to IDR format
    function formatCurrency(value) {
      if (!value && value !== 0) return '0';
      return new Intl.NumberFormat('id-ID').format(value);
    }

    // Determine tier state based on member's current tier
    function getTierState(tierIndex, currentIndex, isLastTier) {
      if (tierIndex < currentIndex) return 'passed';
      if (tierIndex === currentIndex) {
        if (isLastTier && tierIndex === allTiers.value.length - 1) return 'max';
        return 'active';
      }
      return 'locked';
    }

    // Calculate progress percentage for a specific tier
    function calculateTierProgress(tierIndex, currentIndex, tierData, nextTierData) {
      // Tier sudah dilewati
      if (tierIndex < currentIndex) return 100;
      
      // Tier belum tercapai (locked)
      if (tierIndex > currentIndex) return 0;
      
      // Active tier - calculate actual progress
      const tierMin = parseFloat(tierData?.transaction_value_min) || 0;
      const nextMin = parseFloat(nextTierData?.transaction_value_min) || tierMin;
      
      // Jika tidak ada tier berikutnya atau sudah max tier
      if (!nextTierData || nextMin <= tierMin) return 100;
      
      // Hitung progress dari tier minimum ke tier berikutnya
      const range = nextMin - tierMin;
      const currentProgress = totalTransaction.value - tierMin;
      
      // Pastikan progress tidak negatif dan tidak lebih dari 100%
      if (currentProgress <= 0) return 0;
      if (currentProgress >= range) return 100;
      
      return (currentProgress / range) * 100;
    }

    // Build tier progress list for rendering
    const tierProgressList = computed(() => {
      if (!allTiers.value.length) return [];

      const sortedTiers = [...allTiers.value].sort((a, b) => parseFloat(a.transaction_value_min) - parseFloat(b.transaction_value_min));
      const currentIndex = sortedTiers.findIndex(t => t.name === currentTierName.value);
      
      let mappedTiers = [];

      if (currentIndex === -1) {
         const nonTier = {
            id: 'non-tier',
            name: currentTierName.value || 'Member',
            transaction_value_min: 0,
            icon: { image_url: 'fas fa-medal' },
            benefits: []
         };
         mappedTiers.push(nonTier);
         mappedTiers.push(...sortedTiers);
      } else {
         mappedTiers = sortedTiers;
      }

      const actualCurrentIndex = currentIndex === -1 ? 0 : currentIndex;
      const isAtMaxTier = actualCurrentIndex === mappedTiers.length - 1;

      return mappedTiers.map((tier, index) => {
        const nextTier = mappedTiers[index + 1] || null;
        const state = getTierState(index, actualCurrentIndex, isAtMaxTier);
        const progressPercentage = calculateTierProgress(index, actualCurrentIndex, tier, nextTier);

        return {
          id: tier.id,
          name: tier.name,
          state,
          progressPercentage,
          currentAmount: state === 'active' ? formatCurrency(totalTransaction.value) : 
                         state === 'passed' ? formatCurrency(tier.transaction_value_min) : '0',
          currentTierData: {
            name: tier.name,
            amount: `IDR. ${formatCurrency(tier.transaction_value_min)}`,
            icon: tier.icon?.image_url || (index === actualCurrentIndex && currentIndex === -1 ? 'fas fa-medal' : '/img/member-img/tier-gold-example.svg')
          },
          nextTierData: nextTier ? {
            name: nextTier.name,
            amount: `IDR. ${formatCurrency(nextTier.transaction_value_min)}`,
            icon: nextTier.icon?.image_url || '/img/member-img/tier-platinum-example.svg'
          } : {
            name: 'MAX',
            amount: 'Maximum Tier',
            icon: tier.icon?.image_url || '/img/member-img/tier-platinum-example.svg'
          },
          benefits: tier.benefits || []
        };
      });
    });

    // Get selected tier name for benefits header
    const selectedTierName = computed(() => {
      if (tierProgressList.value[selectedCardIndex.value]) {
        return tierProgressList.value[selectedCardIndex.value].name;
      }
      return currentTierName.value || 'Member';
    });

    // Get selected tier data for desktop card display
    const selectedTierData = computed(() => {
      return tierProgressList.value[selectedCardIndex.value] || null;
    });

    // Check if current displayed benefits are locked (from next tier)
    const isBenefitLocked = computed(() => {
      const selectedTier = tierProgressList.value[selectedCardIndex.value];
      if (!selectedTier) return false;
      return selectedTier.state === 'locked';
    });

    // Display benefits based on selected tier
    const displayedBenefits = computed(() => {
      const selectedTier = tierProgressList.value[selectedCardIndex.value];
      if (!selectedTier) return benefits.value;

      // Current / passed / max tier → show current benefits (includes all-tiers)
      if (selectedTier.state === 'active' || selectedTier.state === 'passed' || selectedTier.state === 'max') {
        return benefits.value;
      }

      // Locked tier → show ONLY that specific tier's benefits (no all-tier bleed)
      const tierData = next_tier_benefits.value.find(t => t.tier_id === selectedTier.id);
      return tierData ? tierData.benefits : [];
    });

    // Group benefits by corporate or branch
    const groupedBenefits = computed(() => {
      const groupMap = {};

      for (const benefit of displayedBenefits.value) {
        let groupId;
        let groupName;
        let isCorporate = false;

        const isCorpBenefit = benefit.origin === 'corporate' || (!benefit.branch_id && !benefit.branch?.id && !benefit.merchant?.branch_id && (benefit.corporate_id || benefit.corporate?.id));

        if (isCorpBenefit) {
          isCorporate = true;
          groupId = benefit.corporate?.id || benefit.corporate_id || corporate.value?.id || 'corporate';
          groupName = benefit.corporate?.name || corporate.value?.name || 'Corporate';
        } else if (benefit.origin === 'branch' || benefit.branch?.id || benefit.branch_id || benefit.merchant?.branch_id) {
          groupId = benefit.branch?.id || benefit.branch_id || benefit.merchant?.branch_id || benefit.merchant?.branch?.id;
          groupName = benefit.branch?.name || benefit.merchant?.branch?.name || 'Branch';
        } else {
          groupId = benefit.merchant?.id || 'general';
          groupName = benefit.merchant?.name || 'General Benefits';
        }

        if (!groupMap[groupId]) {
          groupMap[groupId] = {
            id: groupId,
            name: groupName,
            isCorporate,
            benefits: []
          };
        }
        groupMap[groupId].benefits.push(benefit);
      }

      const groups = Object.values(groupMap);

      // Corporate always on top; 'general' next; rest sorted by count desc
      groups.sort((a, b) => {
        if (a.isCorporate) return -1;
        if (b.isCorporate) return 1;
        if (a.id === 'general') return -1;
        if (b.id === 'general') return 1;
        return b.benefits.length - a.benefits.length || a.name.localeCompare(b.name);
      });

      return groups;
    });

    const openGroupId = ref(null);

    // Reset open accordion on list change
    watch(groupedBenefits, () => {
      openGroupId.value = null;
    });

    function handleToggle(groupId) {
      openGroupId.value = openGroupId.value === groupId ? null : groupId;
    }

    function isBenefitGroupLocked() {
      return isBenefitLocked.value;
    }

    async function loadDashboardData() {
      loading.value = true;
      // Store current selected tier before reload (only if not first load)
      const previousSelectedIndex = isFirstLoad.value ? null : selectedCardIndex.value;
      
      try {
        const filter = {};
        if (selectedMerchant.value) {
          filter.merchant_id = selectedMerchant.value;
        }
        store.commit('ClientBenefit/add_filter', { filter });

        const dashboardData = await store.dispatch('ClientBenefit/read');
        const data = dashboardData.data.data;

        // Reset semua data terlebih dahulu untuk menghindari nilai cache lama
        merchants.value = [];
        allTiers.value = [];
        totalTransaction.value = 0;
        pendingTotalTransaction.value = 0;
        currentTierName.value = '';
        corporate.value = null;
        benefits.value = [];
        next_benefits.value = [];
        next_tier_benefits.value = [];

        // Set data baru
        merchants.value = data.benefit_merchants || [];
        allTiers.value = data.tier || [];
        totalTransaction.value = data.total_transaction || 0;
        pendingTotalTransaction.value = data.pending_total_transaction || 0;
        currentTierName.value = data.tier_name || '';
        corporate.value = data.corporate || null;

        // Process benefits
        if (Array.isArray(data.benefit)) {
          benefits.value = [...data.benefit];
        } else if (typeof data.benefit === 'object') {
          benefits.value = Object.values(data.benefit);
        } else {
          benefits.value = [];
        }

        next_benefits.value = data.next_benefit || [];
        next_tier_benefits.value = data.next_tier_benefits || [];

        // Calculate progress text
        const currentIndex = allTiers.value.findIndex(t => t.name === currentTierName.value);
        const nextTier = allTiers.value[currentIndex + 1];
        
        if (currentIndex === allTiers.value.length - 1) {
          progressText.value = 'You have reached the highest tier!';
        } else if (nextTier) {
          const remaining = nextTier.transaction_value_min - totalTransaction.value;
          if (remaining > 0) {
            progressText.value = `You're ${formatCurrency(remaining)} away from the next tier.`;
          } else {
            progressText.value = 'You have reached the highest tier!';
          }
        }

        // Set active tier index
        activeTierIndex.value = currentIndex >= 0 ? currentIndex : 0;
        
        // On first load, always select active tier. On subsequent loads, restore previous selection
        if (isFirstLoad.value) {
          selectedCardIndex.value = activeTierIndex.value;
        } else if (previousSelectedIndex !== null && previousSelectedIndex >= 0 && previousSelectedIndex < allTiers.value.length) {
          selectedCardIndex.value = previousSelectedIndex;
        } else {
          selectedCardIndex.value = activeTierIndex.value;
        }

        // Load user data
        const userId = data.member_profile?.user_id;
        if (userId) {
          await loadUserState(userId);
        }

        // Scroll to selected tier after DOM update
        await nextTick();
        setTimeout(() => {
          scrollToCard(selectedCardIndex.value);
        }, 300);

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        loading.value = false;
        isFirstLoad.value = false;
      }
    }

    async function loadUserState(userId) {
      try {
        const userData = await store.dispatch('User/read_single', userId);
        user.title = userData.title || '';
        user.first_name = userData.first_name || '';
        user.last_name = userData.last_name || '';
        image.url = userData.user_profile?.avatar?.image_url || store.state.Auth.userInfo?.member?.member_avatar || '';
        imageErrorStatus.value = false;
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    }

    function handleScroll() {
      const container = isMobile.value ? tierProgressContainerMobile.value : tierProgressContainer.value;
      if (!container) return;

      const cards = container.querySelectorAll('.tier-progress-container');
      
      if (!cards.length) return;

      const containerCenter = container.scrollLeft + container.offsetWidth / 2;

      let closestIndex = 0;
      let closestDistance = Infinity;

      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const distance = Math.abs(containerCenter - cardCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      selectedCardIndex.value = closestIndex;
    }

    function scrollToCard(index) {
      const container = isMobile.value ? tierProgressContainerMobile.value : tierProgressContainer.value;
      if (!container) return;

      const cards = container.querySelectorAll('.tier-progress-container');
      
      if (!cards[index]) return;

      const card = cards[index];
      const containerWidth = container.offsetWidth;
      const cardLeft = card.offsetLeft;
      const cardWidth = card.offsetWidth;
      
      const scrollPosition = cardLeft - (containerWidth / 2) + (cardWidth / 2);
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }

    function selectTier(index) {
      selectedCardIndex.value = index;
      scrollToCard(index);
    }

    function openTierModal() {
      isTierModalOpen.value = true;
    }

    function closeTierModal() {
      isTierModalOpen.value = false;
    }

    function selectTierFromModal(index) {
      selectedCardIndex.value = index;
      closeTierModal();
    }

    function goBack() {
      const allowedRoutes = [
        '/client/dashboard',
        '/client/transaction-history',
        '/client/voucher',
        '/client/deals'
      ];

      // Check if there's history and if previous route is allowed
      if (window.history.length > 1) {
        const previousRoute = router.options.history.state.back;
        
        // If previous route exists and is in allowed list, go back
        if (previousRoute && allowedRoutes.some(route => previousRoute.includes(route))) {
          router.back();
          return;
        }
      }
      
      // Default: go to dashboard
      router.push('/client/dashboard');
    }

    function goToEditProfile() {
      router.push('/client/account');
    }

    function openFilterModal() {
      isFilterModalOpen.value = true;
    }

    function closeFilterModal() {
      isFilterModalOpen.value = false;
    }

    function applyFilter() {
      loadDashboardData();
      closeFilterModal();
    }

    function clearFilter() {
      selectedMerchant.value = '';
      loadDashboardData();
      closeFilterModal();
    }

    function applyFilterDesktop() {
      loadDashboardData();
    }

    function clearFilterDesktop() {
      selectedMerchant.value = '';
      loadDashboardData();
    }

    function handleBenefitSelect(benefit) {
      selectedBenefitDetail.value = benefit;
      isBenefitModalOpen.value = true;
    }

    function closeBenefitModal() {
      isBenefitModalOpen.value = false;
      selectedBenefitDetail.value = null;
    }

    onMounted(() => {
      loadDashboardData();
    });

    onBeforeUnmount(() => {
      document.body.style.overflow = ''
    })

    watch([isFilterModalOpen, isTierModalOpen, isBenefitModalOpen], (vals) => {
      const anyOpen = vals.some(Boolean)
      document.body.style.overflow = anyOpen ? 'hidden' : ''
    })

    return {
      groupedBenefits,
      openGroupId,
      handleToggle,
      isBenefitGroupLocked,
      loading,
      isFirstLoad,
      user,
      image,
      defaultAvatar,
      imageErrorStatus,
      imageError,
      getInitials,
      progressText,
      tierProgressList,
      selectedCardIndex,
      selectedTierName,
      selectedTierData,
      displayedBenefits,
      isBenefitLocked,
      tierProgressContainer,
      tierProgressContainerMobile,
      handleScroll,
      scrollToCard,
      selectTier,
      openTierModal,
      closeTierModal,
      selectTierFromModal,
      goBack,
      goToEditProfile,
      openFilterModal,
      closeFilterModal,
      applyFilter,
      clearFilter,
      applyFilterDesktop,
      clearFilterDesktop,
      handleBenefitSelect,
      closeBenefitModal,
      isFilterModalOpen,
      isTierModalOpen,
      isBenefitModalOpen,
      selectedBenefitDetail,
      isBenefitModuleEnabled,
      selectedMerchant,
      merchants,
      isMobile,
      currentTierName,
      next_tier_benefits,
      pendingTotalTransaction,
      isPendingBannerDismissed,
      dismissPendingBanner,
      formatCurrency,
      corporate
    };
  }
}
</script>


<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.page-container {
  width: 100%;
  min-height: 100vh;
}

/* ==================== DESKTOP VIEW ==================== */
.desktop-view {
  margin: 0 auto;
  padding: 24px;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 14px;
  color: #6B7280;
}

/* ==================== PENDING NOTICE BANNER ==================== */
.desktop-pending-banner {
  margin-bottom: 24px;
}

.mobile-pending-banner {
  margin-bottom: 18px;
}

/* Top Cards Row - 3 Columns */
.top-cards-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 24px;
}

.top-cards-row.two-cols {
  grid-template-columns: repeat(2, 1fr);
}

/* Profile Card Desktop */
.profile-card.desktop {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.profile-card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.profile-img.desktop {
  position: relative;
  width: 80px;
  height: 80px;
  cursor: pointer;
  flex-shrink: 0;
}

.profile-img.desktop .profile-img-wrapper {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid var(--primary-border, #F93E3E);
}

.profile-img.desktop .profile-img-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-img-wrapper .member-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-gradient, linear-gradient(135deg, #F93E3E 0%, #E02020 100%));
  color: #ffffff;
  font-size: 28px;
  font-weight: 600;
  user-select: none;
}

.header-section .profile-img-wrapper .member-avatar-placeholder {
  font-size: 32px;
}

.profile-img.desktop .edit-profile-btn {
  position: absolute;
  right: -4px;
  bottom: -4px;
  border: 3px solid white;
  background-color: var(--primary-color, #F93E3E);
  padding: 6px;
  border-radius: 50%;
  line-height: 0;
  cursor: pointer;
}

.profile-img.desktop .edit-profile-btn img {
  width: 10px !important;
  height: 10px !important;
}

.profile-info.desktop {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
}

.profile-name {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.profile-tier {
  font-size: 13px;
  color: #6B7280;
  margin: 0;
}

/* Tier Card */
.tier-card-wrapper {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s;
}

.tier-card-wrapper:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.selected-tier-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tier-select-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  color: #6B7280;
  padding-top: 8px;
  border-top: 1px dashed #E5E7EB;
}

.tier-select-hint svg {
  color: #6B7280;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
}

/* Benefits Section Desktop */
.benefits-section.desktop {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
}

/* Accordion list wrapper — mobile & desktop */
.benefits-accordion-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.benefit-card.desktop {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #F9FAFB;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  cursor: pointer;
  transition: all 0.2s;
}

.benefit-card.desktop:hover {
  background: white;
  border-color: var(--primary-border, #F93E3E);
  box-shadow: 0 4px 12px rgba(249, 62, 62, 0.1);
}

.benefit-card.desktop.locked {
  opacity: 0.7;
}

.benefit-card.desktop.locked:hover {
  border-color: #D1D5DB;
  box-shadow: none;
}

.benefit-icon-wrapper {
  width: 48px;
  min-width: 48px;
  height: 48px;
  background-color: white;
  border-radius: 50%;
  border: 1px solid #E1E4EA;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.benefit-icon-wrapper.locked {
  background-color: #F5F5F5;
  border-color: #D1D5DB;
}

.benefit-icon-mask {
  display: inline-block;
  width: 60%;
  height: 60%;
  background-color: var(--primary-color, #00AA00);
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
}

.benefit-icon-mask.locked {
  background-color: var(--primary-color, #F93E3E);
}

.benefit-info {
  flex: 1;
  min-width: 0;
}

.benefit-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.benefit-merchant {
  font-size: 12px;
  color: #6B7280;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.benefit-arrow {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.benefit-arrow img {
  width: 100%;
  height: 100%;
}

.filter-card.desktop {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 20px;
}

.filter-group {
  margin-bottom: 20px;
}

.filter-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.custom-select.desktop {
  position: relative;
}

.custom-select.desktop select {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  background: white;
  cursor: pointer;
  appearance: none;
  outline: none;
  transition: all 0.2s;
}

.custom-select.desktop select:hover {
  border-color: var(--primary-border, #F93E3E);
}

.custom-select.desktop select:focus {
  border-color: var(--primary-border, #F93E3E);
  box-shadow: 0 0 0 3px var(--secondary-color, rgba(249, 62, 62, 0.1));
}

.custom-select.desktop select:disabled {
  background: #F9FAFB;
  cursor: not-allowed;
}

/* Loading removed - using skeleton instead */

/* Button Group */
.btn-apply,
.btn-clear {
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-apply {
  background-color: var(--primary-color, #F93E3E);
  color: white;
  border: 1px solid transparent;
  transition: all 0.2s ease-in-out;
}

.btn-apply:hover {
  background-color: var(--secondary-color, #E02D2D) !important;
  color: var(--primary-color, #F93E3E);
  border: 1px solid var(--primary-color, #F93E3E);
}

.btn-apply:disabled {
  background-color: var(--secondary-color, #FCA5A5);
  cursor: not-allowed;
}

.btn-clear {
  background-color: white;
  color: #374151;
  border: 1px solid #E5E7EB;
}

.btn-clear:hover {
  background-color: #F9FAFB;
}

.btn-clear:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ==================== MOBILE VIEW ==================== */
.edit-profile-btn {
  position: absolute;
  border: 4px solid white;
  background-color: var(--primary-color, #F93E3E);
  padding: 8px;
  border-radius: 100%;
  line-height: 0;
  right: 0;
  bottom: 0;
}

.edit-profile-btn img {
  width: 12px !important;
  height: 12px !important;
}

.benefits-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.benefits-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.benefits-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #717784;
  font-size: 14px;
  background: white;
  border-radius: 12px;
}

.mobile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mobile-title {
  font-size: 18px;
  font-weight: 520;
  margin: 0 !important;
}

.mobile-actions {
  display: flex;
  gap: 8px;
}

.filter-button {
  background-color: var(--primary-color, #F93E3E);
  border: none;
  padding: 6px 10px;
  border-radius: 8px;
  transition: background-color 0.3s;
  cursor: pointer;
}

.filter-button img {
  width: 14px;
  height: 14px;
}

.tier-progress {
  display: flex;
  gap: 24px;
  align-items: center;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  padding: 24px 12px;
  margin: 0 -12px;
  -webkit-overflow-scrolling: touch;
}

.tier-progress::-webkit-scrollbar {
  display: none;
}

.tier-progress {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.tier-progress > * {
  scroll-snap-align: center;
  flex-shrink: 0;
  cursor: pointer;
}

.back-button {
  border: none;
  padding: 10px;
  background-color: var(--primary-color, #F93E3E);
  border-radius: 12px;
  position: absolute;
  line-height: 0;
  cursor: pointer;
}

.back-button img {
  width: 18px;
  height: 18px;
}

.header-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 280px;
  text-align: center;
}

.header-text h1 {
  font-size: 1rem;
  font-weight: 550;
  line-height: 1.3;
  margin: 0 !important;
  text-align: center;
  word-break: break-word;
  overflow-wrap: break-word;
  width: 100%;
}

.header-text p {
  font-size: 12px;
  color: #717784;
  line-height: 1.3;
  margin: 0 !important;
  text-align: center;
  word-break: break-word;
  overflow-wrap: break-word;
  width: 100%;
}

.profile-img {
  position: relative;
  width: 96px;
  height: 96px;
  cursor: pointer;
}

.profile-img-wrapper {
  width: 100%;
  height: 100%;
  border-radius: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 4px solid white;
}

.profile-img-wrapper img {
  object-fit: cover;
  width: 110%;
  height: 110%;
}

.header-section {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 320px;
  gap: 12px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 12px;
  align-items: center;
}

.top-background {
  position: absolute;
  top: 0;
  right: 0;
  z-index: -100;
  color: var(--primary-color, #F93E3E);
}

.spinner-position {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
}

/* Filter Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: white;
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #E5E7EB;
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.close-button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6B7280;
}

.modal-body {
  padding: 20px;
}

.filter-group {
  margin-bottom: 20px;
}

.filter-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.custom-select {
  position: relative;
}

.custom-select select {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  background: white;
  cursor: pointer;
  appearance: none;
  outline: none;
  transition: all 0.2s;
}

.custom-select select:hover {
  border-color: var(--primary-border, #F93E3E);
}

.custom-select select:focus {
  border-color: var(--primary-border, #F93E3E);
  box-shadow: 0 0 0 3px var(--secondary-color, rgba(249, 62, 62, 0.1)) !important;
}

.select-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #6B7280;
}

.button-group {
  display: flex;
  flex-direction: row;
  gap: 12px;
}

.btn-apply,
.btn-clear {
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-apply {
  background-color: var(--primary-color, #F93E3E);
  color: white;
}

.btn-apply:hover {
  background-color: #E02D2D;
}

.btn-clear {
  background-color: white;
  color: #374151;
  border: 1px solid #E5E7EB;
}

.btn-clear:hover {
  background-color: #F9FAFB;
}

/* Select Icon */
.select-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #6B7280;
}

/* Mobile Custom Select */
.custom-select {
  position: relative;
}

.custom-select select {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  background: white;
  cursor: pointer;
  appearance: none;
  outline: none;
  transition: all 0.2s;
}

.custom-select select:hover {
  border-color: #D1D5DB;
}

.custom-select select:focus {
  border-color: var(--primary-border, #F93E3E);
  box-shadow: 0 0 0 3px rgba(249, 62, 62, 0.1);
}

/* Filter Modal Styles (Mobile) */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: white;
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #E5E7EB;
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.close-button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6B7280;
}

.modal-body {
  padding: 20px;
}

/* Desktop Tier Modal */
.modal-overlay.desktop-modal {
  align-items: center;
}

.tier-modal {
  border-radius: 16px;
  max-width: 500px;
  max-height: 80vh;
}

.tier-modal-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 24px;
}

.tier-modal-item {
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.2s;
  padding: 8px;
  border: 2px solid transparent;
}

.tier-modal-item:hover {
  background: #F9FAFB;
}

.tier-modal-item.selected {
  border-color: var(--primary-border, #F93E3E);
  background: var(--secondary-color, #FEF2F2);
}

/* ==================== RESPONSIVE ==================== */
@media (max-width: 991px) {
  .desktop-view {
    display: none;
  }
  
  .mobile-view {
    display: block;
    padding-bottom: 96px;
  }
}

@media (min-width: 992px) {
  .desktop-view {
    display: block;
  }
  
  .mobile-view {
    display: none;
  }
}

@media (min-width: 992px) {
  .filter-card.desktop .button-group {
    flex-direction: column;
  }
}

@media (min-width: 992px) and (max-width: 1280px) {
  .top-cards-row {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .top-cards-row.two-cols {
    grid-template-columns: repeat(2, 1fr);
  }

  .benefits-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
  
  .desktop-view {
    padding: 16px;
  }
}

@media (min-width: 1025px) {
  .benefits-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 250px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px dashed #cbd5e1;
    padding: 2rem;
    margin-top: 1rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

.empty-feature-state {
    opacity: 0.7;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 12px;
}

.empty-feature-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 0;
}

.empty-feature-content i {
    font-size: 32px;
    opacity: 0.5;
}
</style>
